/**
 * ChangePasswordPage Component
 *
 * Page for users to change their password.
 * Handles both voluntary password changes and forced changes (mustChangePassword).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, Container } from '../components/layout';
import { Button, Card, CardBody, CardHeader, Input, FormField } from '../components/ui';
import {
  PasswordStrengthIndicator,
  validatePasswordStrength,
} from '../components/auth/PasswordStrengthIndicator';
import { useAuth, getDashboardPath } from '../contexts/AuthContext';
import { authApi } from '../services/authApi';
import { useRateLimitTimer } from '../hooks/useRateLimitTimer';

export const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser, isLoading: authLoading } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isRateLimited, secondsRemaining, handleError: handleRateLimitError } = useRateLimitTimer();

  // Determine if this is a forced password change
  const isForcedChange = user?.mustChangePassword === true;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Prevent submission while rate limited
    if (isRateLimited) {
      return;
    }

    // Validate current password
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }

    // Validate new password
    if (!newPassword) {
      setError('New password is required');
      return;
    }

    // Validate password strength
    const strengthResult = validatePasswordStrength(newPassword);
    if (!strengthResult.valid) {
      setError('New password does not meet all requirements');
      return;
    }

    // Validate password confirmation
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Ensure new password is different from current
    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await authApi.changePassword(currentPassword, newPassword);

      if (result.success) {
        setSuccess(true);

        // Refresh user data to clear mustChangePassword flag
        await refreshUser();

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          if (user) {
            navigate(getDashboardPath(user.role), { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        }, 2000);
      } else {
        setError('Failed to change password. Please try again.');
      }
    } catch (err) {
      const errorMsg = handleRateLimitError(err);
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Only allow cancel if not a forced password change
    if (!isForcedChange && user) {
      navigate(getDashboardPath(user.role));
    }
  };

  // Show loading state while checking session
  if (authLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 'var(--space-4)',
          backgroundColor: 'var(--color-bg)',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid var(--color-border, #000)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <Layout>
      <Container maxWidth="sm">
        <div
          className="flex flex-col items-center justify-center"
          style={{ minHeight: '60vh', padding: 'var(--space-12) 0' }}
        >
          <Card style={{ width: '100%' }}>
            <CardHeader>
              <h2
                style={{
                  textAlign: 'center',
                  margin: 0,
                  fontFamily: 'var(--font-body)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Change Password
              </h2>
            </CardHeader>

            <CardBody>
              {isForcedChange && (
                <div
                  style={{
                    padding: 'var(--space-3) var(--space-4)',
                    backgroundColor: 'var(--warning-bg)',
                    border: 'var(--border-thick)',
                    borderColor: '#f59e0b',
                    marginBottom: 'var(--space-6)',
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-sm)',
                  }}
                  role="alert"
                >
                  <strong>Password change required</strong>
                  <br />
                  You must change your password before continuing.
                </div>
              )}

              {success ? (
                <div
                  style={{
                    padding: 'var(--space-4) var(--space-6)',
                    backgroundColor: '#d1fae5',
                    border: 'var(--border-thick)',
                    borderColor: 'var(--color-success)',
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      margin: '0 auto var(--space-4)',
                      backgroundColor: 'var(--color-success)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3
                    style={{
                      fontFamily: 'var(--font-body)',
                      textTransform: 'uppercase',
                      marginBottom: 'var(--space-2)',
                    }}
                  >
                    Password Changed
                  </h3>
                  <p
                    style={{
                      fontFamily: 'var(--font-body)',
                      color: 'var(--color-muted)',
                      margin: 0,
                    }}
                  >
                    Redirecting to dashboard...
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {(error || isRateLimited) && (
                    <div
                      style={{
                        padding: 'var(--space-3) var(--space-4)',
                        backgroundColor: isRateLimited ? 'var(--warning-bg)' : 'var(--danger-bg)',
                        border: 'var(--border-thick)',
                        borderColor: isRateLimited ? '#f59e0b' : 'var(--color-error)',
                        marginBottom: 'var(--space-6)',
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: isRateLimited ? '#b45309' : 'var(--color-error)',
                      }}
                      role="alert"
                    >
                      {isRateLimited ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                          <span>Too many attempts. Please wait </span>
                          <span style={{
                            fontFamily: 'var(--font-mono)',
                            fontWeight: 700,
                            backgroundColor: '#fbbf24',
                            padding: '2px 8px',
                            borderRadius: '4px',
                          }}>
                            {secondsRemaining}s
                          </span>
                        </div>
                      ) : (
                        error
                      )}
                    </div>
                  )}

                  <FormField
                    label="Current Password"
                    required
                    htmlFor="currentPassword"
                    className="mb-4"
                  >
                    <Input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter your current password"
                      autoComplete="current-password"
                      autoFocus
                      disabled={isSubmitting || isRateLimited}
                    />
                  </FormField>

                  <FormField
                    label="New Password"
                    required
                    htmlFor="newPassword"
                    className="mb-4"
                  >
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter your new password"
                      autoComplete="new-password"
                      disabled={isSubmitting || isRateLimited}
                    />
                    <PasswordStrengthIndicator password={newPassword} />
                  </FormField>

                  <FormField
                    label="Confirm New Password"
                    required
                    htmlFor="confirmPassword"
                    className="mb-6"
                  >
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your new password"
                      autoComplete="new-password"
                      disabled={isSubmitting || isRateLimited}
                      error={
                        confirmPassword && newPassword !== confirmPassword
                          ? 'Passwords do not match'
                          : undefined
                      }
                    />
                    {confirmPassword && newPassword === confirmPassword && (
                      <div
                        style={{
                          marginTop: 'var(--space-2)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          color: 'var(--color-success)',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Passwords match
                      </div>
                    )}
                  </FormField>

                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--space-4)',
                      flexDirection: isForcedChange ? 'column' : 'row',
                    }}
                  >
                    {!isForcedChange && (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={isSubmitting || isRateLimited}
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      loading={isSubmitting}
                      disabled={isSubmitting || isRateLimited}
                      style={{ flex: isForcedChange ? undefined : 1, width: isForcedChange ? '100%' : undefined }}
                    >
                      {isRateLimited
                        ? `Wait ${secondsRemaining}s`
                        : isSubmitting
                          ? 'Changing Password...'
                          : 'Change Password'}
                    </Button>
                  </div>
                </form>
              )}

              {!success && (
                <div
                  style={{
                    marginTop: 'var(--space-6)',
                    paddingTop: 'var(--space-6)',
                    borderTop: 'var(--border-thin)',
                    textAlign: 'center',
                  }}
                >
                  <p
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--color-muted)',
                      margin: 0,
                    }}
                  >
                    {user?.email && (
                      <>
                        Logged in as: <strong>{user.email}</strong>
                      </>
                    )}
                  </p>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </Container>
    </Layout>
  );
};

export default ChangePasswordPage;
